import { useState } from "react";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { IoIosArrowRoundBack } from "react-icons/io";
import QuestionSection from "../../ui/QuestionSection/QuestionSection";
import CommonFooter from "../../CommonFooter/CommonFooter";
import CommonButton from "../../ui/CommonButton/CommonButton";
import InputField from "../../ui/InputField/InputField";
import classes from "./Introduction.module.css";
import { useAccreditation } from "../../../hooks/useAccreditation";

const Introduction = () => {
  const { labId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [currentSet, setCurrentSet] = useState(0);
  const [isEditing, setIsEditing] = useState(false);

  const { formData, setFormData, loading, hydrated, fetching, editable, setEditable, handleDone } =
    useAccreditation({
      labId,
      section: "Introduction",
      name: "SADCAS F134-A",
      auditorId: user._id,
      isAssessor: user.role === "ASSESSOR",
      isEditing,
      token,
    });

  const handleAnswerChange = (questionId, score, comment, points, content) => {
    setFormData((prev) => ({
      ...prev,
      [questionId]: { score, comment, points, content },
    }));
  };

  const handleFormFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        [field]: value,
      },
    }));
  };

  const questionSets = [
    {
      questions: [
        {
          questionId: "Q1",
          content:
            "The schedule of accredited tests shall include only the tests that are performed by the Laboratory.",
          number: "1",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q2",
          content: "The relevant staff have access to SADCAS documentation",
          number: "2",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q3",
          content:
            "A current list of approved signatories for each discipline within the laboratory is available.",
          number: "3",
          multiline: true,
          placeholder: "Add comment",
        },
      ],
      render: (onAnswerChange, answers) => (
        <QuestionSection
          number="1"
          title="Introduction"
          accreditation
          showPointsField={false}
          questions={questionSets[0].questions}
          onAnswerChange={editable ? onAnswerChange : () => {}}
          answers={answers}
          disabled={!editable}
        />
      ),
    },
  ];

  const handleNext = () => {
    if (currentSet < questionSets.length - 1) setCurrentSet((prev) => prev + 1);
  };

  const handlePrevious = () => {
    if (currentSet > 0) setCurrentSet((prev) => prev - 1);
  };

  if (!hydrated || fetching) {
    return <div className={classes.loading}>Loading...</div>;
  }

  return (
    <div className={`${classes.wrapper} ${!editable ? classes.fromParams : ""}`}>
      <div className="d-flex justify-content-between align-items-center">
        <div
          className="d-flex align-items-center gap-2 mb-4"
          onClick={() => navigate(-1)}
          role="button"
        >
          <IoIosArrowRoundBack size={35} />
          <h2 className="m-0">Introduction</h2>
        </div>

        {user.role === "ASSESSOR" && !editable && (
          <CommonButton
            className={classes.editButton}
            onClick={() => {
              setIsEditing(true);
              setEditable(true);
            }}
            title="Edit"
          />
        )}
      </div>

      {!editable ? (
        <div className={classes.infoText}>
          ✅ Your SADCAS A - Introduction has been submitted.
        </div>
      ) : (
        <>
          {/* Extra form fields */}
          <div className="row mb-5">
            <div className="col-6 mb-3">
              <InputField
                labelText="Assessor/s"
                placeholder="Enter username"
                value={formData.metadata?.assessor || ""}
                onChange={(e) => handleFormFieldChange("assessor", e.target.value)}
                disabled={!editable}
              />
            </div>
            <div className="col-6 mb-3">
              <InputField
                labelText="Observers"
                placeholder="Enter Observers"
                value={formData.metadata?.observers || ""}
                onChange={(e) => handleFormFieldChange("observers", e.target.value)}
                disabled={!editable}
              />
            </div>
            <div className="col-6 mb-3">
              <InputField
                labelText="Area/Field of operation"
                placeholder="Enter area"
                value={formData.metadata?.areaOfOperation || ""}
                onChange={(e) => handleFormFieldChange("areaOfOperation", e.target.value)}
                disabled={!editable}
              />
            </div>
            <div className="col-6 mb-3">
              <InputField
                labelText="Laboratory representative"
                placeholder="Enter representative"
                value={formData.metadata?.labRepresentative || ""}
                onChange={(e) => handleFormFieldChange("labRepresentative", e.target.value)}
                disabled={!editable}
              />
            </div>
          </div>

          {questionSets[currentSet].render(handleAnswerChange, formData)}
        </>
      )}

      {editable && (
        <CommonFooter
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          handleDone={handleDone}
          cancelText="Cancel"
          handleCancel={() => setEditable(false)}
          currentSet={currentSet}
          questionSets={questionSets}
          doneText={loading ? "Submitting..." : "Update"}
          disabled={loading}
        />
      )}
    </div>
  );
};

export default Introduction;